import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { MIN_INSTALLMENTS } from "@/constants/payroll-installments";
import { IManagerCandidate } from "@/@types/divergence";
import { DivergenceStatusEnum } from "@/enum/divergence-status";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useUpdateDivergenceStatusMutation } from "@/services/divergence";
import { useManualIdentityFallback } from "../../hooks/use-manual-identity-fallback";
import { EmployeeRegistryPanel } from "./employee-registry-panel";
import { EmploymentNoticeBanner } from "./employment-notice-banner";
import { IdentityRuleAcknowledge } from "./identity-rule-acknowledge";
import { InstallmentsField } from "./installments-field";
import { ManualIdentityFallback } from "./manual-identity-fallback";
import {
  consumeOwnDecision,
  rememberOwnDecisions,
} from "../../utils/own-decisions";
import { AlertTriangle, Undo2 } from "lucide-react";
import { useState } from "react";

import {
  BATCH_PREFLIGHT_LABELS,
  DECISION_LABELS,
  DECISION_REASON_MAX_LENGTH,
  DECISION_REASON_MIN_LENGTH,
  IDENTITY_QUEUE_LABELS,
  RESPONSIBLE_CONFIRM_LABELS,
} from "../../utils/messages";
import {
  EmploymentNotice,
  employmentNoticeOf,
  confirmInformedLabel,
  identityChoiceIsComplete,
  onlyDigits,
} from "../../utils/manual-identity";
import { apiErrorMessage } from "../../utils/api-error-message";
import {
  blocksPayrollDiscount,
  PayrollAmountCandidate,
} from "../../utils/batch-preflight";
import {
  consequenceOf,
  DecisionStatus,
} from "../../utils/decision-consequence";
import { isPayrollDiscount } from "../../utils/is-payroll-discount";
import { DECISION_REASON_TEST_ID } from "../../utils/decision-test-ids";

export const DECISION_CONSEQUENCE_TEST_ID: string = "decision-consequence";

export const DECISION_INSTALLMENTS_TEST_ID: string = "decision-installments";

export const DECISION_INSTALLMENTS_PREVIEW_TEST_ID: string =
  "decision-installments-preview";

export const DECISION_BLOCKED_TEST_ID: string = "decision-blocked";

export const DECISION_RESPONSIBLE_TEST_ID: string = "decision-responsible";

export const DECISION_RESPONSIBLE_ERROR_TEST_ID: string =
  "decision-responsible-error";

const DECISION_REASON_FIELD_ID: string = "decision-reason-field";

const DECISION_REASON_HINT_ID: string = "decision-reason-hint";

export type DecisionSubject = PayrollAmountCandidate;

export interface DecisionQueueControls {
  position: number;
  total: number;
  onResolved: () => void;
  onSkip: () => void;
}

interface DecisionConfirmationModalProps {
  divergenceId: number;
  divergence: DecisionSubject;
  action: DecisionStatus;
  initialInstallments?: number;
  queue?: DecisionQueueControls;
}

const CONFIRM_LABEL: Record<DecisionStatus, string> = {
  [DivergenceStatusEnum.APPROVED]: DECISION_LABELS.CONFIRM_APPROVE,
  [DivergenceStatusEnum.REJECTED]: DECISION_LABELS.CONFIRM_REJECT,
};

const SUCCESS_MESSAGE: Record<DecisionStatus, string> = {
  [DivergenceStatusEnum.APPROVED]: DECISION_LABELS.SUCCESS_APPROVE,
  [DivergenceStatusEnum.REJECTED]: DECISION_LABELS.SUCCESS_REJECT,
};


export function DecisionConfirmationModal({
  divergenceId,
  divergence,
  action,
  initialInstallments,
  queue,
}: DecisionConfirmationModalProps): JSX.Element {
  const { handleModal } = useDialogModal();
  const [updateStatus, { isLoading }] = useUpdateDivergenceStatusMutation();
  const [reason, setReason] = useState<string>("");
  const [showReasonError, setShowReasonError] = useState<boolean>(false);
  const [cpf, setCpf] = useState<string>("");
  const [chosen, setChosen] = useState<string>("");
  const [acknowledgedFor, setAcknowledgedFor] = useState<string>("");
  const [ruleAckFor, setRuleAckFor] = useState<string>("");
  const [installments, setInstallments] = useState<number>(
    initialInstallments ?? MIN_INSTALLMENTS,
  );

  const isApproval: boolean = action === DivergenceStatusEnum.APPROVED;

  const consequence: string = consequenceOf(action, divergence);

  const isBlocked: boolean = blocksPayrollDiscount(divergence, action);

  const needsInstallments: boolean =
    isApproval && !isBlocked && isPayrollDiscount(divergence);

  const needsIdentityConfirmation: boolean = needsInstallments;

  const manualIdentity = useManualIdentityFallback(divergenceId, {
    enabled: needsIdentityConfirmation,
  });

  const notice: EmploymentNotice | null = manualIdentity.resolved
    ? employmentNoticeOf(manualIdentity.resolved.employment)
    : null;

  const ruleAcknowledged: boolean =
    Boolean(manualIdentity.resolved) &&
    ruleAckFor === manualIdentity.resolved?.identity.cpf;

  const ruleNeedsAck: boolean =
    Boolean(manualIdentity.resolved) &&
    manualIdentity.resolved?.source !== "INFORMED";

  const identityReady: boolean =
    !needsIdentityConfirmation ||
    (Boolean(manualIdentity.resolved) && (!ruleNeedsAck || ruleAcknowledged));

  const identityByCpf: boolean = Boolean(manualIdentity.block);

  const candidates: IManagerCandidate[] =
    manualIdentity.block?.candidates ?? [];

  const informedIsComplete: boolean = identityChoiceIsComplete({
    candidates,
    preview: manualIdentity.preview,
    chosen,
    acknowledgedFor,
  });

  const confirmLabel: string = identityByCpf
    ? confirmInformedLabel(candidates.length > 0, manualIdentity.candidatesFailed)
    : CONFIRM_LABEL[action];

  const confirmDisabled: boolean =
    isLoading || isBlocked || (identityByCpf ? !informedIsComplete : !identityReady);

  const confirmInformed = async (): Promise<void> => {
    if (chosen && !manualIdentity.candidatesFailed) {
      await submit({ operatorCode: chosen });
      return;
    }

    await submit({ cpf: onlyDigits(cpf) });
  };

  const chooseCandidate = (operatorCode: string): void => {
    setChosen(operatorCode);
    void manualIdentity.chooseCandidate(operatorCode);
  };

  const trimmedReason: string = reason.trim();
  const reasonIsValid: boolean =
    isApproval || trimmedReason.length >= DECISION_REASON_MIN_LENGTH;

  const closeModal = (): void => {
    handleModal({ isOpen: false });
  };

  const finish = (): void => {
    if (queue) {
      queue.onResolved();
      return;
    }

    closeModal();
  };

  const dismiss = (): void => {
    if (queue) {
      queue.onSkip();
      return;
    }

    closeModal();
  };

  const confirm = async (): Promise<void> => {
    if (isBlocked) return;

    if (!reasonIsValid) {
      setShowReasonError(true);
      return;
    }

    await submit();
  };

  const submit = async (manual?: {
    cpf?: string;
    operatorCode?: string;
  }): Promise<void> => {
    try {
      rememberOwnDecisions([divergenceId]);

      await updateStatus({
        id: divergenceId,
        status: action,
        ...(isApproval ? {} : { reason: trimmedReason }),
        ...(needsInstallments ? { installments_count: installments } : {}),
        ...(manual?.cpf ? { manual_identity_cpf: manual.cpf } : {}),
        ...(manual?.operatorCode
          ? { manual_identity_operator_code: manual.operatorCode }
          : {}),
      }).unwrap();

      toast({
        variant: "success",
        description: SUCCESS_MESSAGE[action],
        duration: 3000,
      });

      finish();
    } catch (error) {
      consumeOwnDecision(divergenceId);

      if (!manual && manualIdentity.offerFor(error)) return;

      toast({
        variant: "destructive",
        description: apiErrorMessage(error) ?? DECISION_LABELS.ERROR,
        duration: 5000,
      });
    }
  };

  return (
    <div className="flex flex-col items-center space-y-6 max-w-lg mx-auto">
      <div
        className={`flex items-center justify-center w-16 h-16 rounded-full ${
          isApproval ? "bg-yellow-100" : "bg-gray-100"
        }`}
      >
        {isApproval ? (
          <AlertTriangle className="w-8 h-8 text-yellow-600" />
        ) : (
          <Undo2 className="w-8 h-8 text-gray-600" />
        )}
      </div>

      {isBlocked ? (
        <p
          className="w-full rounded-md border border-amber-300 bg-amber-50 p-3 text-center text-sm text-amber-800"
          data-testid={DECISION_BLOCKED_TEST_ID}
        >
          {BATCH_PREFLIGHT_LABELS.SKIP_REASON.NON_NEGATIVE_AMOUNT}
        </p>
      ) : (
        <p
          className="text-sm text-gray-700 text-center"
          data-testid={DECISION_CONSEQUENCE_TEST_ID}
        >
          {consequence}
        </p>
      )}

      {needsIdentityConfirmation && (
        <div
          className="w-full space-y-3"
          data-testid={DECISION_RESPONSIBLE_TEST_ID}
        >
          {identityByCpf ? (
            <ManualIdentityFallback
              blockMessage={manualIdentity.block?.message ?? ""}
              candidates={candidates}
              knownOperator={manualIdentity.block?.knownOperator ?? null}
              preview={manualIdentity.preview}
              isSearching={manualIdentity.isSearching}
              searchError={manualIdentity.searchError}
              cpf={cpf}
              chosen={chosen}
              acknowledgedFor={acknowledgedFor}
              candidatesFailed={manualIdentity.candidatesFailed}
              onCpfChange={setCpf}
              onChosenChange={chooseCandidate}
              onAcknowledgeChange={setAcknowledgedFor}
              onSearch={(value: string): void => void manualIdentity.search(value)}
            />
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {RESPONSIBLE_CONFIRM_LABELS.INTRO}
              </p>

              {manualIdentity.isDescribing && (
                <div
                  aria-label={RESPONSIBLE_CONFIRM_LABELS.LOADING}
                  role="status"
                  className="h-40 w-full animate-pulse rounded-md border bg-muted/40"
                />
              )}

              {manualIdentity.resolved && (
                <EmployeeRegistryPanel preview={manualIdentity.resolved} />
              )}

              {notice && <EmploymentNoticeBanner notice={notice} />}

              {manualIdentity.resolved && (
                <IdentityRuleAcknowledge
                  source={manualIdentity.resolved.source}
                  acknowledged={ruleAcknowledged}
                  onAcknowledge={(value: boolean): void =>
                    setRuleAckFor(
                      value
                        ? (manualIdentity.resolved?.identity.cpf ?? "")
                        : "",
                    )
                  }
                />
              )}

              {Boolean(manualIdentity.resolvedError) && (
                <p
                  role="alert"
                  data-testid={DECISION_RESPONSIBLE_ERROR_TEST_ID}
                  className="w-full rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
                >
                  {manualIdentity.resolvedError}
                </p>
              )}

              {manualIdentity.resolved && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={manualIdentity.startReplacement}
                >
                  {RESPONSIBLE_CONFIRM_LABELS.REPLACE}
                </Button>
              )}
            </>
          )}
        </div>
      )}

      {needsInstallments && (
        <InstallmentsField
          id={DECISION_INSTALLMENTS_TEST_ID}
          amount={divergence.amount}
          value={installments}
          disabled={isLoading}
          showHint
          onChange={setInstallments}
        />
      )}

      {!isApproval && (
        <div className="w-full space-y-1">
          <label
            htmlFor={DECISION_REASON_FIELD_ID}
            className="text-sm font-medium text-gray-700"
          >
            {DECISION_LABELS.REASON_LABEL}
          </label>
          <Textarea
            id={DECISION_REASON_FIELD_ID}
            data-testid={DECISION_REASON_TEST_ID}
            value={reason}
            onChange={event => setReason(event.target.value)}
            placeholder={DECISION_LABELS.REASON_PLACEHOLDER}
            maxLength={DECISION_REASON_MAX_LENGTH}
            disabled={isLoading}
            aria-invalid={showReasonError && !reasonIsValid}
            aria-describedby={DECISION_REASON_HINT_ID}
            rows={3}
          />
          <p
            id={DECISION_REASON_HINT_ID}
            className={`text-xs ${
              showReasonError && !reasonIsValid
                ? "text-red-600"
                : "text-gray-500"
            }`}
          >
            {showReasonError && !reasonIsValid
              ? DECISION_LABELS.REASON_TOO_SHORT
              : DECISION_LABELS.REASON_HINT}
          </p>
        </div>
      )}

      <div className="flex space-x-3 w-full">
        <Button
          variant="outline"
          onClick={dismiss}
          disabled={isLoading}
          className="flex-1"
        >
          {queue ? IDENTITY_QUEUE_LABELS.SKIP : DECISION_LABELS.CANCEL}
        </Button>
        <Button
          onClick={identityByCpf ? confirmInformed : confirm}
          disabled={confirmDisabled}
          className="flex-1"
          variant={isApproval ? "default" : "destructive"}
        >
          {isLoading ? DECISION_LABELS.PROCESSING : confirmLabel}
        </Button>
      </div>
    </div>
  );
}
